/**
 * Primary hook for ViewJob to access all Ghostwriter-related data.
 *
 * Encapsulates:
 * - Proctor gating (rn_ghostwriter_test must be active)
 * - Eligibility lookup (from the shared autoAppliableJobKeys set)
 * - Application data fetch (always re-fetches on every render)
 * - Consent status resolution (via useResolveConsentStatus)
 * - Button state computation for ViewJob CTA rendering
 *
 * ViewJob-first scenario: if the eligibility set hasn't been hydrated
 * by Homepage yet, falls back to a single-job eligibility + application
 * fetch.
 *
 * Phase B variant: when the user is in the `phase_b` bucket, the proctor
 * payload's `jobKeys` array is the source of truth for eligibility, so the
 * lifecycle pre-hydrates `autoAppliableJobKeys` from it and this hook
 * skips the per-job eligibility backend call entirely.
 */

import { loadingAsyncState, successAsyncState, errorAsyncState } from './AsyncState';
import { useGhostwriterApi } from './ghostwriterApi';
import {
    useIsJobAutoAppliable,
    useGhostwriterJobByKey,
} from './GhostwriterContext';
import {
    addJobToStore,
    addJobKeyToAutoAppliableSet,
    setJobsState,
    getSnapshot,
} from './GhostwriterStore';
import { useGhostwriterGate } from './useGhostwriterGate';
import { useResolveConsentStatus } from './useResolveConsentStatus';
import { type ScheduleApplyJob, type ScheduleApplyStatus, ScheduleApplyStatuses, ScheduleApplyStatusSeverity } from '../models/ScheduleApplyJob';

import { useCallback, useEffect, useMemo } from 'react';

export type GhostwriterButtonAction =
    | 'none'
    | 'loading'
    | 'try-auto-apply'
    | 'auto-apply'
    | 'in-progress'
    | 'ready-for-review'
    | 'applied'
    | 'error';

export const GHOSTWRITER_PRIMARY_ACTIONS: GhostwriterButtonAction[] = [
    'try-auto-apply',
    'auto-apply',
    'in-progress',
    'ready-for-review',
    'applied',
];

export function shouldShowSecondaryApply(buttonAction: GhostwriterButtonAction): boolean {
    return buttonAction !== 'applied' && GHOSTWRITER_PRIMARY_ACTIONS.includes(buttonAction);
}

const STATUS_ACTION_MAP = new Map<ScheduleApplyStatus, GhostwriterButtonAction>([
    [ScheduleApplyStatuses.NotStarted, 'auto-apply'],
    [ScheduleApplyStatuses.AutoApplyCancelled, 'auto-apply'],
    [ScheduleApplyStatuses.AnalyzingJob, 'in-progress'],
    [ScheduleApplyStatuses.FillingOutApplication, 'in-progress'],
    [ScheduleApplyStatuses.SubmittingApplication, 'in-progress'],
    [ScheduleApplyStatuses.RetryPendingCaptcha, 'in-progress'],
    [ScheduleApplyStatuses.ReadyToReview, 'ready-for-review'],
    [ScheduleApplyStatuses.MissingRequiredInfo, 'ready-for-review'],
    [ScheduleApplyStatuses.Applied, 'applied'],
    [ScheduleApplyStatuses.AutoApplyFailed, 'error'],
    [ScheduleApplyStatuses.SubmissionFailed, 'error'],
]);

function computeButtonAction(
    isEnabled: boolean | null,
    isAutoAppliable: boolean,
    hasConsented: boolean,
    consentLoading: boolean,
    ghostwriterJob: ScheduleApplyJob | null,
    appliableLoading: boolean,
): GhostwriterButtonAction {
    if (isEnabled !== true) return 'none';
    if (appliableLoading) return 'loading';
    if (!isAutoAppliable) return 'none';
    if (consentLoading) return 'loading';
    if (!hasConsented) return 'try-auto-apply';
    if (!ghostwriterJob) return 'auto-apply';

    const { applicationStatus } = ghostwriterJob;
    return STATUS_ACTION_MAP.get(applicationStatus)
        ?? (applicationStatus.severity === ScheduleApplyStatusSeverity.Error ? 'error' : 'in-progress');
}

export interface GhostwriterViewJobResult {
    buttonAction: GhostwriterButtonAction;
    applicationId: string | null;
    /**
     * True if the user is in the phase A (synchronous flow) bucket.
     * Consumers branch CTA copy on this flag — see RN-3662.
     */
    isPhaseA: boolean;
    startAutoApply: () => Promise<void>;
}

export function useGhostwriterForViewJob(
    jobKey: string | null,
): GhostwriterViewJobResult {
    const { isEnabled, isPhaseA, isPhaseB } = useGhostwriterGate();
    const { fetchApplicationForJob, fetchEligibilityAndApplication, startScheduleApply } = useGhostwriterApi();
    const { isAutoAppliable, loading: appliableLoading, hydrated } = useIsJobAutoAppliable(jobKey);
    const { job: ghostwriterJob } = useGhostwriterJobByKey(jobKey);
    const { hasConsented, consentLoading } = useResolveConsentStatus(isEnabled === true);

    useEffect(() => {
        if (!isEnabled || !jobKey) return;

        if (hydrated && isAutoAppliable) {
            setJobsState(loadingAsyncState(getSnapshot().jobs.data));
            fetchApplicationForJob(jobKey)
                .then((job) => {
                    if (job) {
                        addJobToStore(job);
                    } else {
                        setJobsState(successAsyncState(getSnapshot().jobs.data ?? []));
                    }
                })
                .catch((err: Error) => setJobsState(errorAsyncState(err, getSnapshot().jobs.data)));
        } else if (!appliableLoading && !isPhaseB) {
            // Phase B's payload is the source of truth for eligibility,
            // so we never fall back to the backend call for it.
            setJobsState(loadingAsyncState(getSnapshot().jobs.data));
            fetchEligibilityAndApplication(jobKey)
                .then((result) => {
                    if (result.eligible) {
                        addJobKeyToAutoAppliableSet(jobKey);
                        if (result.application) {
                            addJobToStore(result.application);
                        } else {
                            setJobsState(successAsyncState(getSnapshot().jobs.data ?? []));
                        }
                    } else {
                        setJobsState(successAsyncState(getSnapshot().jobs.data ?? []));
                    }
                })
                .catch((err: Error) => setJobsState(errorAsyncState(err, getSnapshot().jobs.data)));
        }
    }, [isEnabled, isPhaseB, jobKey, hydrated, isAutoAppliable, appliableLoading, fetchApplicationForJob, fetchEligibilityAndApplication]);

    const startAutoApply = useCallback(async () => {
        if (!jobKey) throw new Error('Cannot start auto apply without a job key');
        await startScheduleApply(jobKey);
    }, [jobKey, startScheduleApply]);

    const buttonAction = useMemo(() => {
        return computeButtonAction(
            isEnabled,
            isAutoAppliable,
            hasConsented,
            consentLoading,
            ghostwriterJob,
            appliableLoading,
        );
    }, [isEnabled, isAutoAppliable, hasConsented, consentLoading, ghostwriterJob, appliableLoading]);

    return {
        buttonAction,
        applicationId: ghostwriterJob?.applicationId ?? null,
        isPhaseA: isPhaseA === true,
        startAutoApply,
    };
}
